import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { CatMeme } from '@/mocks/catMemes';
import MemeShareButtons from './MemeShareButtons';

interface MemeCardProps {
  meme: CatMeme;
  index: number;
}

export default function MemeCard({ meme, index }: MemeCardProps) {
  const [liked, setLiked] = useState(false);
  const [likeCount, setLikeCount] = useState(meme.likes);
  const [showCaption, setShowCaption] = useState(false);

  const handleLike = () => {
    if (!liked) {
      setLiked(true);
      setLikeCount((c) => c + 1);
    } else {
      setLiked(false);
      setLikeCount((c) => c - 1);
    }
  };

  const formatCount = (n: number) => {
    if (n >= 1000) return `${(n / 1000).toFixed(1)}k`;
    return String(n);
  };

  return (
    <motion.div
      id={`meme-${meme.id}`}
      initial={{ opacity: 0, y: 24 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4, delay: index * 0.06 }}
      className="bg-white rounded-2xl overflow-hidden shadow-md hover:shadow-xl transition-shadow duration-300 flex flex-col group"
    >
      {/* Image */}
      <div
        className="relative overflow-hidden cursor-pointer"
        onClick={() => setShowCaption((v) => !v)}
      >
        <img
          src={meme.image}
          alt={meme.title}
          className="w-full aspect-square object-cover object-top group-hover:scale-105 transition-transform duration-500"
        />

        {/* Badges */}
        <div className="absolute top-3 left-3 flex gap-2">
          {meme.isHot && (
            <span className="bg-rose-500 text-white text-xs font-bold px-2.5 py-1 rounded-full flex items-center gap-1">
              <i className="ri-fire-fill"></i> Hot
            </span>
          )}
          {meme.isFeatured && (
            <span className="bg-amber-400 text-white text-xs font-bold px-2.5 py-1 rounded-full flex items-center gap-1">
              <i className="ri-star-fill"></i> Featured
            </span>
          )}
        </div>

        {/* Week badge */}
        <div className="absolute top-3 right-3">
          <span className="bg-black/50 backdrop-blur-sm text-white text-xs font-semibold px-2.5 py-1 rounded-full">
            {meme.week}
          </span>
        </div>

        {/* Caption overlay */}
        <AnimatePresence>
          {showCaption && (
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 10 }}
              className="absolute inset-0 bg-black/60 flex items-end p-4"
            >
              <p className="text-white font-semibold text-base leading-snug">
                {meme.caption}
              </p>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Tap hint */}
        {!showCaption && (
          <div className="absolute bottom-3 right-3 bg-black/40 backdrop-blur-sm text-white text-xs px-2 py-1 rounded-full opacity-0 group-hover:opacity-100 transition-opacity">
            Tap for caption
          </div>
        )}
      </div>

      {/* Body */}
      <div className="p-4 flex flex-col gap-3 flex-1">
        <div>
          <h2 className="font-bold text-gray-800 text-base leading-tight mb-1">{meme.title}</h2>
          <div className="flex flex-wrap gap-1.5">
            {meme.tags.map((tag) => (
              <span key={tag} className="text-xs text-teal-600 bg-teal-50 px-2 py-0.5 rounded-full font-medium">
                #{tag}
              </span>
            ))}
          </div>
        </div>

        {/* Stats row */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button
              onClick={handleLike}
              className={`flex items-center gap-1.5 text-sm font-semibold transition-all cursor-pointer ${
                liked ? 'text-rose-500 scale-110' : 'text-gray-400 hover:text-rose-400'
              }`}
            >
              <motion.i
                className={liked ? 'ri-heart-fill' : 'ri-heart-line'}
                animate={liked ? { scale: [1, 1.4, 1] } : {}}
                transition={{ duration: 0.3 }}
              />
              {formatCount(likeCount)}
            </button>
            <span className="flex items-center gap-1.5 text-sm text-gray-400">
              <i className="ri-share-forward-line"></i>
              {formatCount(meme.shares)}
            </span>
          </div>
          <span className="text-xs font-semibold text-orange-500 bg-orange-50 px-2.5 py-1 rounded-full">
            {meme.category}
          </span>
        </div>

        {/* Share buttons */}
        <div className="pt-1 border-t border-gray-100">
          <MemeShareButtons title={meme.title} memeId={meme.id} compact />
        </div>
      </div>
    </motion.div>
  );
}
